import { type RefObject, useEffect } from "react";

/**
 * Focuses the referenced input when `/` is pressed,
 * unless the user is already typing in an input/textarea
 * or a dialog is open. Escape blurs and clears.
 */
export function useSlashFocus(
	ref: RefObject<HTMLInputElement | null>,
	onClear?: () => void,
) {
	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			const tag = (e.target as HTMLElement)?.tagName;
			const isEditable =
				tag === "INPUT" ||
				tag === "TEXTAREA" ||
				(e.target as HTMLElement)?.isContentEditable;

			if (e.key === "/" && !e.metaKey && !e.ctrlKey && !isEditable) {
				e.preventDefault();
				ref.current?.focus();
			}

			if (e.key === "Escape" && document.activeElement === ref.current) {
				onClear?.();
				ref.current?.blur();
			}
		};
		document.addEventListener("keydown", handler);
		return () => document.removeEventListener("keydown", handler);
	}, [ref, onClear]);
}
